import React, { FC, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  USAGE_METRIC,
  USAGE_METRIC_META,
  USAGE_WARNING_THRESHOLD,
  formatResetDate,
  getUsageRatio,
} from 'constants/usageLimits'
import { SUPERVISION_MODE } from 'constants/supervisionMode'
import { MetricUsage } from 'hooks/useBotUsage'
import { supervisionConfigToMode } from 'tabs/home/api/usageLimits'
import { getCachedBotPackageAssignment } from 'tabs/home/helpers/orgPackages'

import RequestLimitIncreaseModal from './RequestLimitIncreaseModal'
import SupervisionModeCard from './SupervisionModeCard'
import * as S from './UsageMeter.style'
import { MeterTone } from './UsageMeter.style'

interface Props {
  metric: string
  usage: MetricUsage | null
  periodEnd?: string
  botId: string | number
  /** Hidden for CareBrain operators, who change the limit directly instead. */
  canRequestIncrease?: boolean
  /** Package/bot feature flag — disables the request CTA when false. */
  featureEnabled?: boolean
}

const toneFor = (usage: MetricUsage): MeterTone => {
  if (usage.unlimited) return 'normal'
  if (usage.exhausted) return 'spent'
  return getUsageRatio(usage.used, usage.limit) >= USAGE_WARNING_THRESHOLD ? 'warning' : 'normal'
}

// Same Lucide glyph used by the Monthly-limits section: clipboard-check for
// care plan audits. Supervision's icon lives in SupervisionModeCard instead —
// this component's isSupervisionMode branch returns before MeterGlyph runs.
const ICON_PATHS: Record<string, string[]> = {
  [USAGE_METRIC.CARE_PLAN_AUDITS]: [
    'M9 4h6a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z',
    'M16 5h1a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h1',
    'M9 14l2 2 4-4',
  ],
}

const MeterGlyph: FC<{ metric: string }> = ({ metric }) => {
  const paths = ICON_PATHS[metric]
  if (!paths) return null
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {paths.map((d, i) => (
        <path key={i} d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </svg>
  )
}

/**
 * One metered allowance, as a card: what is left in figures, a bar beneath it,
 * the spend and reset date in the footer, and the way to ask for more.
 *
 * It leads with what is left rather than what has been spent — this is the
 * customer's own budget, and "3 left" answers the question they actually
 * arrived with. The used/limit pair stays in the footer, where it explains the
 * headline number without competing with it.
 *
 * Renders nothing when `usage` is null. That is the point: an unread counter
 * shown as "0 of 5" would read as "you have five left", which is a claim the
 * client cannot make when the backend did not answer.
 */
export const UsageMeter: FC<Props> = ({
  metric,
  usage,
  periodEnd,
  botId,
  canRequestIncrease = true,
  featureEnabled = true,
}) => {
  const activeBot = useSelector((state: any) => state.activeBot)
  const adminUser = useSelector((state: any) => state.adminUser)
  const [requestOpen, setRequestOpen] = useState(false)

  // Supervision has no allowance to meter — Dynamic vs Default is a
  // configuration choice, not something spent and refilled — so its card
  // never reaches the bar/footer/Request-increase rendering below. `usage`
  // still gates whether the card shows at all: it is the same signal every
  // other card uses for "this org has this feature".
  const isSupervisionMode = metric === USAGE_METRIC.SUPERVISION_SESSIONS

  if (!usage) return null
  // Package feature off → omit the whole card (not only disable CTA).
  if (!featureEnabled) return null

  const meta = USAGE_METRIC_META[metric]

  if (isSupervisionMode) {
    const assignment =
      activeBot?.packageDetails || getCachedBotPackageAssignment(botId) || null
    const supervisionMode =
      supervisionConfigToMode(assignment?.supervision) ||
      activeBot?.supervisionMode ||
      SUPERVISION_MODE.DEFAULT

    return (
      <SupervisionModeCard
        metric={metric}
        meta={meta}
        mode={supervisionMode}
        botId={botId}
        botName={activeBot?.name}
        requestedBy={adminUser?.email}
        canRequestIncrease={canRequestIncrease}
        featureEnabled={featureEnabled}
      />
    )
  }

  const tone = toneFor(usage)
  const ratio = getUsageRatio(usage.used, usage.limit)
  // The metric's own noun, not a generic one: "6 sessions left" says what is
  // left, where "6 requests left" names the way you ask for more of it.
  const noun = usage.remaining === 1 ? meta?.noun : meta?.nounPlural
  const resets = `resets ${formatResetDate(periodEnd)}`

  return (
    <>
      <S.MeterCard>
        <S.MeterCardBody>
          <S.CardHead>
            <S.CardHeadLeft>
              <S.MeterIcon>
                <MeterGlyph metric={metric} />
              </S.MeterIcon>

              <S.MeterIdentity>
                <S.MeterLabel>{meta?.label || metric}</S.MeterLabel>
              </S.MeterIdentity>
            </S.CardHeadLeft>
          </S.CardHead>

          {usage.unlimited ? (
            <>
              <S.StatRow>
                <S.StatValue tone="normal">{usage.used}</S.StatValue>
                <S.StatLabel>{usage.used === 1 ? meta?.noun : meta?.nounPlural} used this month</S.StatLabel>
              </S.StatRow>
              <S.FootMetaBlock>
                <S.FootMeta>{`No limit · ${resets}`}</S.FootMeta>
              </S.FootMetaBlock>
            </>
          ) : (
            <>
              <S.StatRow>
                <S.StatValue tone={tone}>{usage.remaining}</S.StatValue>
                <S.StatLabel>{noun} left this month</S.StatLabel>
              </S.StatRow>

              <S.TrackRow>
                <S.MeterTrack
                  role="progressbar"
                  aria-label={`${meta?.label || metric}: ${usage.remaining} left of ${usage.limit}`}
                  aria-valuenow={usage.used}
                  aria-valuemin={0}
                  aria-valuemax={usage.limit ?? undefined}>
                  <S.MeterFill ratio={ratio} tone={tone} />
                </S.MeterTrack>
                <S.FootMeta>{`${usage.used} / ${usage.limit} used · ${resets}`}</S.FootMeta>
              </S.TrackRow>
            </>
          )}
        </S.MeterCardBody>

        {canRequestIncrease && (!usage.unlimited || !featureEnabled) && (
          <S.RequestButton
            spent={usage.exhausted}
            disabled={!featureEnabled}
            onClick={() => featureEnabled && setRequestOpen(true)}>
            Request increase
          </S.RequestButton>
        )}
      </S.MeterCard>

      {requestOpen && (
        <RequestLimitIncreaseModal
          open={requestOpen}
          onClose={() => setRequestOpen(false)}
          botId={botId}
          botName={activeBot?.name}
          requestedBy={adminUser?.email}
          metric={metric}
          currentLimit={usage.limit}
        />
      )}
    </>
  )
}

export default UsageMeter
